import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DoctorsPage = () => {
  const { user, sessionToken } = useUnifiedAuth();
  const isAdmin = user?.user_type === 'admin';
  const [doctors, setDoctors] = useState([]);
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('all');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Check if this is an online therapy request
  const isOnlineTherapy = searchParams.get('online_therapy') === 'true';
  const urlSpecialty = searchParams.get('specialty');

  const handleToggleListing = async (doctor) => {
    const isCurrentlyListed = doctor.is_listed !== false;
    const action = isCurrentlyListed ? 'remove from website' : 'add to website';
    
    if (!window.confirm(`Are you sure you want to ${action} this doctor?`)) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/edit-user/${doctor.user_id || doctor.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_listed: !isCurrentlyListed })
      });

      if (response.ok) {
        alert(`✅ Successfully ${!isCurrentlyListed ? 'listed' : 'unlisted'} doctor`);
        // Refresh the doctors list
        fetchDoctors();
      } else {
        const errorData = await response.json();
        alert(`❌ Failed: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error toggling listing:', error);
      alert('❌ Error updating listing status');
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    // Set initial filters based on URL parameters
    if (urlSpecialty && doctors.length > 0) {
      const normalizedSpecialty = urlSpecialty.toLowerCase();
      
      // Handle different variations of psychiatry/mental health specialties
      if (normalizedSpecialty === 'psychiatry' || normalizedSpecialty === 'mental health') {
        setSpecialtyFilter('Psychiatry');
      } else if (normalizedSpecialty === 'psychology') {
        setSpecialtyFilter('Psychology');
      } else {
        // Try to find an exact match in our comprehensive list
        const comprehensiveSpecialties = [
          'General Medicine', 'Cardiology', 'Dermatology', 'Pediatrics', 'Orthopedics',
          'Orthopedic Surgery', 'Obstetrics & Gynecology', 'Neurology', 'Psychiatry', 
          'Psychology', 'ENT (Ear, Nose & Throat)', 'Ophthalmology', 'Dentistry',
          'Cosmetic Surgery', 'Plastic Surgery', 'Anesthesiology', 'Emergency Medicine',
          'Family Medicine', 'Internal Medicine', 'Gastroenterology', 'Endocrinology',
          'Rheumatology', 'Pulmonology', 'Nephrology', 'Hematology', 'Oncology',
          'Urology', 'Radiology', 'Pathology', 'Physical Medicine & Rehabilitation',
          'Sports Medicine', 'Pain Management', 'Infectious Disease', 'Allergy & Immunology'
        ];
        
        const matchingSpecialty = comprehensiveSpecialties.find(s => 
          s.toLowerCase() === normalizedSpecialty
        );
        if (matchingSpecialty) {
          setSpecialtyFilter(matchingSpecialty);
        }
      }
    }
  }, [urlSpecialty, doctors]);

  useEffect(() => {
    filterDoctors();
  }, [doctors, searchTerm, specialtyFilter]);

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      
      let doctorsData = [];
      
      if (isOnlineTherapy) {
        // For online therapy, get mental health professionals from healthcare_providers collection
        try {
          const response = await axios.get(`${API}/healthcare-providers?search=psychiatry`);
          
          if (response.data && response.data.providers) {
            // Map healthcare providers to doctor format for consistency
            doctorsData = response.data.providers.map(provider => ({
              ...provider,
              // Ensure consistent field names
              specialty: provider.specialty || provider.speciality,
              speciality: provider.specialty || provider.speciality,
              consultation_fee: provider.consultation_fee || '50', // Default fee if not specified
              experience_years: provider.experience_years || '5+', // Default experience
              rating: provider.rating || 4.5 // Default rating
            }));
            
            console.log('Fetched online therapy providers:', doctorsData.length, 'providers');
          }
          
        } catch (error) {
          console.error('Error fetching online therapy providers:', error);
          doctorsData = [];
        }
      } else {
        // For general doctors page, get all doctors
        try {
          const response = await axios.get(`${API}/doctors`);
          doctorsData = Array.isArray(response.data) ? response.data : (response.data.doctors || []);
          console.log('Fetched regular doctors:', doctorsData.length, 'doctors');
        } catch (error) {
          console.error('Error fetching doctors:', error);
          doctorsData = [];
        }
      }
      
      setDoctors(doctorsData);
      setFilteredDoctors(doctorsData);
    } catch (error) {
      console.error('Error fetching doctors:', error);
      setDoctors([]);
      setFilteredDoctors([]);
    } finally {
      setLoading(false);
    }
  };

  const filterDoctors = () => {
    let filtered = [...doctors];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(doctor =>
        doctor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doctor.specialty?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doctor.speciality?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doctor.biography?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doctor.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doctor.sub_specialties?.some(specialty => 
          specialty?.toLowerCase().includes(searchTerm.toLowerCase())
        ) ||
        doctor.hospital_affiliations?.some(hospital => 
          hospital.name?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Specialty filter
    if (specialtyFilter !== 'all') {
      filtered = filtered.filter(doctor => 
        doctor.specialty?.toLowerCase().includes(specialtyFilter.toLowerCase()) ||
        doctor.speciality?.toLowerCase().includes(specialtyFilter.toLowerCase())
      );
    }

    // For Online Therapy, filter based on selected specialty or show all mental health professionals if no specific filter
    if (isOnlineTherapy) {
      // If a specific specialty is selected (and it's not 'all'), respect that filter
      // Otherwise, show all mental health professionals
      if (specialtyFilter === 'all') {
        // Show all mental health professionals when no specific specialty is selected
        filtered = filtered.filter(doctor => {
          const specialtyLower = (doctor.specialty || doctor.speciality || '').toLowerCase();
          const subSpecialties = doctor.sub_specialties || [];
          
          return specialtyLower.includes('psychiatry') || 
                 specialtyLower.includes('psychology') ||
                 specialtyLower.includes('mental health') ||
                 subSpecialties.some(sub => 
                   sub?.toLowerCase().includes('therapy') ||
                   sub?.toLowerCase().includes('counseling') ||
                   sub?.toLowerCase().includes('mental health')
                 );
        });
      }
      // If a specific specialty is selected, the regular specialty filter above already handled it
      
      // Sort psychiatrists first in online therapy mode
      filtered.sort((a, b) => {
        const aIsPsychiatrist = (a.specialty || a.speciality || '').toLowerCase().includes('psychiatry');
        const bIsPsychiatrist = (b.specialty || b.speciality || '').toLowerCase().includes('psychiatry');
        
        if (aIsPsychiatrist && !bIsPsychiatrist) return -1;
        if (!aIsPsychiatrist && bIsPsychiatrist) return 1;
        return 0;
      });
    }

    setFilteredDoctors(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSpecialtyFilter('all');
  };

  const handleBookingClick = (doctor, appointmentType) => {
    // Navigate to booking page with doctor and appointment type data
    navigate('/booking', { 
      state: { 
        doctor,
        appointmentType,
        consultationFee: doctor.consultation_fee
      }
    });
  };

  const handleMessageClick = async (doctor) => {
    if (!user || !sessionToken) {
      // Redirect to login if not authenticated
      navigate('/');
      return;
    }

    try {
      // Create or get existing conversation with doctor
      const response = await axios.post(
        `${API}/conversations`,
        null,
        {
          params: {
            participant_id: doctor.user_id || doctor.id,
            initial_message: `Hi Dr. ${doctor.name}, I would like to inquire about a consultation.`
          },
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data) {
        // Navigate to messages page with the conversation
        navigate('/messages');
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      alert('Unable to start conversation. Please try again later.');
    }
  };

  // Comprehensive list of medical specialties
  const getAllSpecialties = () => {
    // Get actual specialties from doctors (for those that exist in DB)
    const actualSpecialties = [...new Set(doctors.map(doctor => doctor.specialty || doctor.speciality).filter(Boolean))];
    
    // Complete medical specialties list
    const comprehensiveSpecialties = [
      'General Medicine',
      'Cardiology', 
      'Dermatology',
      'Pediatrics',
      'Orthopedics',
      'Orthopedic Surgery',
      'Obstetrics & Gynecology',
      'Neurology',
      'Psychiatry',
      'Psychology',
      'ENT (Ear, Nose & Throat)',
      'Ophthalmology',
      'Dentistry',
      'Cosmetic Surgery',
      'Plastic Surgery',
      'Anesthesiology',
      'Emergency Medicine',
      'Family Medicine',
      'Internal Medicine',
      'Gastroenterology',
      'Endocrinology',
      'Rheumatology',
      'Pulmonology',
      'Nephrology',
      'Hematology',
      'Oncology',
      'Urology',
      'Radiology',
      'Pathology',
      'Physical Medicine & Rehabilitation',
      'Sports Medicine',
      'Pain Management',
      'Infectious Disease',
      'Allergy & Immunology'
    ];

    // Combine and deduplicate, prioritizing actual DB specialties
    const combinedSpecialties = [...new Set([...actualSpecialties, ...comprehensiveSpecialties])];
    
    // Sort alphabetically
    return combinedSpecialties.sort();
  };

  const specialties = getAllSpecialties();

  // Add psychiatry to specialties list if not present (for online therapy filtering)
  if (isOnlineTherapy && !specialties.some(s => s.toLowerCase().includes('psychiatry'))) {
    specialties.unshift('Psychiatry');
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading doctors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isOnlineTherapy ? 'Online Therapy in Kuwait' : 'Top Doctors in Kuwait'}
              </h1>
              <p className="text-gray-600 mt-1">
                {isOnlineTherapy 
                  ? 'Professional mental health support through secure video consultations'
                  : 'Find the best specialist doctors in Kuwait'
                }
              </p>
              {isOnlineTherapy && (
                <div className="mt-3 flex items-center text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg inline-flex">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Secure video consultations available in Arabic & English
                </div>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                isOnlineTherapy 
                  ? 'bg-green-100 text-green-800'
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {filteredDoctors.length} {isOnlineTherapy ? 'therapists' : 'doctors'} found
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search Box */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isOnlineTherapy ? 'Search Mental Health Professionals' : 'Search Doctors'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder={isOnlineTherapy 
                    ? "Search by name, therapy type, or specialization..."
                    : "Search by name, specialty, or hospital..."
                  }
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Specialty Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isOnlineTherapy ? 'Therapy Type' : 'Specialty'}
              </label>
              <select
                value={specialtyFilter}
                onChange={(e) => setSpecialtyFilter(e.target.value)}
                className="w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">
                  {isOnlineTherapy ? 'All Therapy Types' : 'All Specialties'}
                </option>
                {specialties.map((specialty) => (
                  <option key={specialty} value={specialty}>{specialty}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Clear Filters */}
          {(searchTerm || specialtyFilter !== 'all') && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={clearFilters}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>

        {/* Doctors List */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredDoctors.map((doctor) => (
            <div key={doctor.id} className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow">
              <div className="p-6">
                {/* Doctor Header */}
                <div className="flex items-start space-x-4 mb-4">
                  {/* Profile Image */}
                  <div className="flex-shrink-0">
                    {doctor.profile_image ? (
                      <img
                        src={doctor.profile_image}
                        alt={doctor.name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center border-2 border-gray-200">
                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  {/* Doctor Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{doctor.name}</h3>
                    <p className="text-blue-600 font-medium text-sm mb-2">
                      {doctor.specialty || doctor.speciality}
                    </p>
                    <div className="flex items-center space-x-2">
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                        {doctor.experience_years} years experience
                      </span>
                      {doctor.rating && (
                        <div className="flex items-center">
                          <span className="text-yellow-400">★</span>
                          <span className="text-sm font-medium text-gray-700 ml-1">{doctor.rating}</span>
                        </div>
                      )}
                      {isOnlineTherapy && (
                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                          Online Available
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Clinics */}
                {doctor.clinics && doctor.clinics.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Clinics:</h4>
                    <div className="space-y-1">
                      {doctor.clinics.slice(0, 2).map((clinic, index) => (
                        <div key={index} className="flex items-center text-gray-600 text-sm">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <span>{clinic}</span>
                        </div>
                      ))}
                      {doctor.clinics.length > 2 && (
                        <span className="text-gray-500 text-xs ml-6">+{doctor.clinics.length - 2} more clinics</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Board Certifications */}
                {doctor.board_certifications && doctor.board_certifications.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Board Certifications:</h4>
                    <div className="flex flex-wrap gap-1">
                      {doctor.board_certifications.map((cert, index) => (
                        <span key={index} className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Availability Icons */}
                <div className="mb-4">
                  <div className="flex items-center gap-3">
                    {doctor.online_consultation_available && (
                      <div className="flex items-center text-blue-600 text-sm">
                        <span className="text-lg mr-1">🖥️</span>
                        <span>Online Available</span>
                      </div>
                    )}
                    {doctor.in_clinic_available && (
                      <div className="flex items-center text-green-600 text-sm">
                        <span className="text-lg mr-1">🏥</span>
                        <span>In-Clinic Available</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Location */}
                {doctor.location && (
                  <div className="mb-4">
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(doctor.location)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-blue-600 hover:text-blue-700 text-sm transition-colors"
                    >
                      <span className="mr-1">📍</span>
                      <span className="underline">{doctor.location}</span>
                    </a>
                  </div>
                )}

                {/* Sub-specialties */}
                {doctor.sub_specialties && doctor.sub_specialties.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Sub-specialties:</h4>
                    <div className="flex flex-wrap gap-1">
                      {doctor.sub_specialties.slice(0, 2).map((specialty, index) => (
                        <span key={index} className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                          {specialty}
                        </span>
                      ))}
                      {doctor.sub_specialties.length > 2 && (
                        <span className="text-gray-500 text-xs">+{doctor.sub_specialties.length - 2} more</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Hospital Affiliations */}
                {doctor.hospital_affiliations && doctor.hospital_affiliations.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Hospital Affiliations:</h4>
                    {doctor.hospital_affiliations.slice(0, 2).map((affiliation, index) => (
                      <div key={index} className="flex items-center text-gray-600 text-sm mb-1">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="font-medium">{affiliation.name}</span>
                        <span className="text-gray-500 ml-1">({affiliation.position})</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Languages */}
                {doctor.languages && doctor.languages.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center text-gray-500 text-sm">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                      </svg>
                      <span>Languages: {doctor.languages.join(', ')}</span>
                    </div>
                  </div>
                )}

                {/* Procedures Performed */}
                {doctor.procedures_performed && (
                  <div className="mb-4">
                    <div className="flex items-center text-gray-500 text-sm">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{doctor.procedures_performed.toLocaleString()} procedures performed</span>
                    </div>
                  </div>
                )}

                {/* Contact and Booking */}
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center text-gray-600 text-sm">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                      <span>Consultation: KD {doctor.consultation_fee}</span>
                    </div>
                  </div>

                  <div className="flex flex-col space-y-2">
                    {/* Admin List/Unlist Control */}
                    {isAdmin && (
                      <button 
                        onClick={() => handleToggleListing(doctor)}
                        className={`w-full px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                          doctor.is_listed !== false 
                            ? 'border-orange-300 text-orange-700 hover:bg-orange-50' 
                            : 'border-green-300 text-green-700 hover:bg-green-50'
                        }`}
                        title={doctor.is_listed !== false ? 'Remove from website' : 'Add to website'}
                      >
                        {doctor.is_listed !== false ? '🚫 Unlist from Website' : '✅ List on Website'}
                      </button>
                    )}
                    
                    {/* For Online Therapy, prioritize online appointments */}
                    {isOnlineTherapy ? (
                      <>
                        <button 
                          onClick={() => handleBookingClick(doctor, 'online')}
                          className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Start Online Therapy Session
                        </button>
                        <button 
                          onClick={() => handleBookingClick(doctor, 'in-person')}
                          className="w-full border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center justify-center"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Book In-Person Visit
                        </button>
                        <button 
                          onClick={() => handleMessageClick(doctor)}
                          className="w-full border border-green-300 text-green-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-50 transition-colors flex items-center justify-center"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          Send Message
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                          onClick={() => handleBookingClick(doctor, 'in-person')}
                          className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Book Appointment In-Person
                        </button>
                        <button 
                          onClick={() => handleBookingClick(doctor, 'online')}
                          className="w-full border border-blue-300 text-blue-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors flex items-center justify-center"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Book Online Appointment
                        </button>
                        <button 
                          onClick={() => handleMessageClick(doctor)}
                          className="w-full border border-green-300 text-green-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-50 transition-colors flex items-center justify-center"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          Send Message
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredDoctors.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {isOnlineTherapy ? 'No therapists found' : 'No doctors found'}
            </h3>
            <p className="text-gray-600 mb-4">
              {isOnlineTherapy 
                ? 'Try adjusting your search criteria for mental health professionals.'
                : 'Try adjusting your search criteria or clear the filters.'
              }
            </p>
            <button
              onClick={clearFilters}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorsPage;